import { faUserPen, faUserXmark } from '@fortawesome/pro-light-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { PERMISSIONS } from '@/constants/auth.constants';
import { CRITICALITY_LABELS } from '@/constants/criticalitylabels.constants';
import useFetchOrganizationAssetTags from '@/hooks/useAssetTags';
import { usePermissionsCheck } from '@/hooks/usePermissionsCheck';
import { useFetchSettings } from '@/pages/Administration/Administration.store';
import {
  useCreateAsset,
  useFetchAssetTypes,
  useUpdateAsset,
  useUpdateHostIsolationSettings
} from '@/pages/Asset/Asset.store';
import {
  ASSET_TOOLTIP_TEXT,
  type AssetCreateDTO,
  type AssetUpdateDTO,
  DEFAULT_ASSET_FIELDS,
  selectedAssetAtom,
  useFetchAssetDetails
} from '@/pages/Assets';
import { ASSET_DATA_PROVIDERS } from '@/pages/Assets/Assets.constants';
import { extractTagNames } from '@/pages/Assets/Assets.utils';
import {
  selectedOrganizationAtom,
  selectedOrganizationIdAtom
} from '@/pages/Organizations';
import { isLayoutMobileAtom } from '@/store/layout';

import { validateIPAddress } from '../SourceCreate/SourceCreate.utils';
import { COMPLIANCE_OPTIONS } from './AssetAddEditDialog.constants';
import useStyles from './AssetAddEditDialog.styles';
import type {
  AssetEditAddFormData,
  EditAssetDialogProps
} from './AssetAddEditDialog.types';
import { formatDefaultAssetValues } from './AssetAddEditDialog.utils';

const AssetAddEditDialog = ({ open, onClose, type }: EditAssetDialogProps) => {
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const [selectedAsset] = useAtom(selectedAssetAtom);
  const protectedAttributes = selectedAsset?.protected_attributes || [];
  const [updatedProtectedAttributes, setUpdatedProtectedAttributes] =
    useState<string[]>(protectedAttributes);
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const { classes } = useStyles({ isLayoutMobile });
  const defaultFormValues = formatDefaultAssetValues(
    selectedAsset,
    type,
    selectedOrganizationId as number
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<AssetEditAddFormData>({
    defaultValues: defaultFormValues as AssetEditAddFormData
  });
  const { data: assetTypeData } = useFetchAssetTypes();
  const { updateAssetAsync, isLoadingUpdateAssetIntegration } =
    useUpdateAsset();
  const { createAssetAsync, isLoadingCreateAsset } = useCreateAsset();
  const { updateHostIsolationSettingsAsync, isUpdatingHostIsolationSettings } =
    useUpdateHostIsolationSettings();
  const {
    data: assetDetail,
    isFetching: isAssetDetailFetching,
    isError: isAssetDetailError
  } = useFetchAssetDetails();
  const assetDetailReady =
    type !== 'edit' || Boolean(assetDetail && !isAssetDetailError);
  const {
    serverData: organizationSettings,
    isLoading: isSettingsLoading,
    isError: isSettingsError,
    refetch: refetchSettings
  } = useFetchSettings(undefined, { showErrorNotification: false });
  const canUpdateSettings = usePermissionsCheck(PERMISSIONS.ADMIN_UPDATE);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [hostIsolationExcluded, setHostIsolationExcluded] = useState(false);
  const [hasSavedAsset, setHasSavedAsset] = useState(false);
  const [settingsSaveError, setSettingsSaveError] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!open) {
      setHasSavedAsset(false);
      setSettingsSaveError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || type !== 'edit' || !assetDetailReady || !assetDetail) return;
    reset(formatDefaultAssetValues(assetDetail, type, selectedOrganizationId));
    setUpdatedProtectedAttributes(assetDetail.protected_attributes || []);
  }, [
    assetDetail,
    assetDetailReady,
    open,
    reset,
    selectedOrganizationId,
    type
  ]);

  // Fetch organization asset tags for user tag options
  const { data: organizationAssetTags } = useFetchOrganizationAssetTags(
    selectedAsset?.organization_id || 0
  );

  // Get user tag options from organization tags (exclude compliance tags)
  const userTagOptions = useMemo(() => {
    return (
      organizationAssetTags
        ?.filter((tag) => tag.description !== 'compliance_tags')
        ?.map((tag) => tag.tag_name) || []
    );
  }, [organizationAssetTags]);

  // Extract IP mapping tags (fixed options that cannot be removed)
  const ipMappingTagNames = useMemo(
    () => extractTagNames(selectedAsset?.ip_mapping_tags || []),
    [selectedAsset?.ip_mapping_tags]
  );

  const watchedAssetName = useWatch({ control, name: 'asset_name' });
  const selectedComplianceTags = useWatch({ control, name: 'compliance_tags' });
  const selectedAssetType = useWatch({ control, name: 'asset_type' });
  const selectedUserTags = useWatch({ control, name: 'user_tags' }) || [];

  // Combine fixed IP mapping tags with user-editable tags
  const combinedTagsValue = useMemo(
    () => [...ipMappingTagNames, ...selectedUserTags],
    [ipMappingTagNames, selectedUserTags]
  );

  // All available tag options (fixed + user options, no duplicates)
  const allTagOptions = useMemo(
    () => [
      ...ipMappingTagNames,
      ...userTagOptions.filter((option) => !ipMappingTagNames.includes(option))
    ],
    [ipMappingTagNames, userTagOptions]
  );

  const dataProvider = selectedAsset?.external_details?.[0]?.provider;

  const assetTypes = useMemo(
    () => Object.keys(assetTypeData || {}),
    [assetTypeData]
  );

  const assetSubTypes: string[] = useMemo(() => {
    if (!selectedAssetType) return [''];
    return sortBy(Object.values(assetTypeData?.[selectedAssetType] || {}));
  }, [assetTypeData, selectedAssetType]);

  const IS_ALL_ORG = selectedOrganizationId === 0;

  const handleComplianceTagsChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const newComplianceTags =
      typeof value === 'string' ? value.split(',') : value;

    setValue('compliance_tags', newComplianceTags);

    // Add to protected attributes when user makes an edit
    if (
      type === 'edit' &&
      !updatedProtectedAttributes.includes('compliance_tags')
    ) {
      setUpdatedProtectedAttributes([
        ...updatedProtectedAttributes,
        'compliance_tags'
      ]);
    }

    event.stopPropagation();
  };

  const handleTagDelete = (tagToDelete: string) => {
    const updatedComplianceTags = selectedComplianceTags?.filter(
      (tag) => tag !== tagToDelete
    );
    setValue('compliance_tags', updatedComplianceTags);
  };

  const onSubmit = async (data: AssetEditAddFormData) => {
    if (type === 'edit' && !assetDetailReady) return;
    setSettingsSaveError(null);

    const submittedAssetName =
      type === 'add' ? data.asset_name : assetDetail?.asset_name;
    const hostName = submittedAssetName?.trim().toLocaleLowerCase() ?? '';
    const currentHostNames =
      organizationSettings?.host_isolation_exclusions?.excluded_host_names ??
      [];
    const wasExcluded = currentHostNames.some(
      (name) => name.toLocaleLowerCase() === hostName
    );
    const settingsPayload =
      organizationSettings && hostName && wasExcluded !== hostIsolationExcluded
        ? {
            host_isolation_exclusions: {
              excluded_host_names: hostIsolationExcluded
                ? [
                    ...currentHostNames.filter(
                      (name) => name.toLocaleLowerCase() !== hostName
                    ),
                    hostName
                  ]
                : currentHostNames.filter(
                    (name) => name.toLocaleLowerCase() !== hostName
                  ),
              excluded_asset_types:
                organizationSettings.host_isolation_exclusions
                  ?.excluded_asset_types ?? [],
              excluded_asset_subtypes:
                organizationSettings.host_isolation_exclusions
                  ?.excluded_asset_subtypes ?? [],
              excluded_asset_tags:
                organizationSettings.host_isolation_exclusions
                  ?.excluded_asset_tags ?? []
            }
          }
        : null;

    try {
      if (type === 'add' && !hasSavedAsset) {
        const createProtectedAttributes: string[] = Object.keys(
          DEFAULT_ASSET_FIELDS
        ).map((field) => field);
        const createAssetData = {
          ...data,
          group_names: [],
          protected_attributes: createProtectedAttributes
        };
        await createAssetAsync(createAssetData as AssetCreateDTO);
      }
      if (type === 'edit') {
        const updatedData: Partial<AssetUpdateDTO> = {};

        Object.entries(DEFAULT_ASSET_FIELDS).forEach(([key, value]) => {
          const typedKey = key as keyof AssetEditAddFormData;
          const dataValue = data[typedKey];

          // Add protected attributes to updated data
          updatedData.protected_attributes = updatedProtectedAttributes;

          // Get existing value with special handling for tag fields
          let existingValue: unknown;
          if (key === 'user_tags') {
            existingValue = extractTagNames(selectedAsset?.user_tags);
          } else if (key === 'compliance_tags') {
            existingValue = extractTagNames(selectedAsset?.compliance_tags);
          } else {
            existingValue = selectedAsset?.[key as keyof typeof selectedAsset];
          }

          // Only update if the value has changed or is not the default value
          if (dataValue !== undefined && existingValue !== dataValue) {
            // Handle array comparison for fields like compliance_tags and tags
            if (Array.isArray(dataValue) && Array.isArray(value)) {
              (updatedData as Record<string, unknown>)[key] = dataValue;
            } else {
              (updatedData as Record<string, unknown>)[key] = dataValue;
            }
          }
        });

        await updateAssetAsync(updatedData as AssetUpdateDTO);
        setHasSavedAsset(true);
      }
    } catch {
      // Asset hooks own the existing asset-specific error notification.
      return;
    }

    if (settingsPayload && selectedOrganization?.label) {
      try {
        await updateHostIsolationSettingsAsync({
          organizationLabel: selectedOrganization.label,
          payload: settingsPayload
        });
      } catch {
        setSettingsSaveError(
          'The asset was saved, but its host-isolation exclusion setting could not be updated. Please try saving again.'
        );
        return;
      }
    }

    setHasSavedAsset(false);
    setSettingsSaveError(null);
    onClose();
  };

  const handleCancel = () => {
    setHasSavedAsset(false);
    setSettingsSaveError(null);
    reset(); // Reset form to default values
    onClose();
  };

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    // Reset asset subtype when asset type changes
    setValue('asset_subtype', '');
    setValue('asset_type', event.target.value);
    // Add to protected attributes when user makes an edit
    if (type === 'edit' && !updatedProtectedAttributes.includes('asset_type')) {
      setUpdatedProtectedAttributes([
        ...updatedProtectedAttributes,
        'asset_type'
      ]);
    }
  };

  const handleSubTypeChange = (event: SelectChangeEvent<string>) => {
    setValue('asset_subtype', event.target.value);
    // Add to protected attributes when user makes an edit
    if (
      type === 'edit' &&
      !updatedProtectedAttributes.includes('asset_subtype')
    ) {
      setUpdatedProtectedAttributes([
        ...updatedProtectedAttributes,
        'asset_subtype'
      ]);
    }
  };

  const dialogTitle = useMemo(() => {
    return type === 'edit'
      ? `Edit Asset: ${selectedAsset?.asset_name}`
      : 'Add Asset';
  }, [type, selectedAsset]);

  const excludedHostNames =
    organizationSettings?.host_isolation_exclusions?.excluded_host_names ?? [];
  const assetName = (
    type === 'add'
      ? watchedAssetName
      : (assetDetail?.asset_name ?? selectedAsset?.asset_name ?? '')
  )
    .trim()
    .toLocaleLowerCase();
  const isHostNameExcluded = excludedHostNames.some(
    (hostName) => hostName.toLocaleLowerCase() === assetName
  );

  useEffect(() => {
    if (
      !open ||
      !organizationSettings ||
      (type === 'edit' && !assetDetailReady)
    )
      return;
    setHostIsolationExcluded(isHostNameExcluded);
  }, [assetDetailReady, isHostNameExcluded, open, organizationSettings, type]);

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <IconButton
        className={classes.assetAddEditDialogIconButtonCancel}
        onClick={handleCancel}
      >
        <FontAwesomeIcon size="xs" icon={faXmark} />
      </IconButton>
      <DialogContent>
        {IS_ALL_ORG && (
          <Alert severity="info" sx={{ my: 4 }}>
            <Typography variant="body1">
              Please select an organization to edit an Asset.
            </Typography>
          </Alert>
        )}
        {!IS_ALL_ORG && type === 'edit' && (
          <Typography variant="subtitle1">
            When you modify a field, the value will be preserved, and automatic
            updates for that property will stop. To resume automatic syncing for
            any field, simply click the reset button next to it - your manual
            changes will be replaced from the source system on the next sync.
          </Typography>
        )}
        {!IS_ALL_ORG && (
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Form Fields */}
            <Stack
              className={classes.formFieldsContainer}
              direction="row"
              sx={{ alignItems: 'flex-start' }}
            >
              {type === 'add' && (
                <Box className={classes.assetHalfWidthField}>
                  <InputLabel required htmlFor="asset-name">
                    Asset Name
                  </InputLabel>
                  <Controller
                    name="asset_name"
                    control={control}
                    rules={{ required: 'Asset Name is required' }}
                    render={({ field }) => (
                      <FormControl id="asset-name" fullWidth>
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          error={!!errors.asset_name}
                          helperText={errors.asset_name?.message}
                        />
                      </FormControl>
                    )}
                  />
                </Box>
              )}
              {/* Ip Address Field */}
              <Box className={classes.assetHalfWidthField}>
                <InputLabel required={type === 'add'} htmlFor="ip-address">
                  IP Address
                </InputLabel>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center' }}
                  spacing={2}
                >
                  <Controller
                    name="ip_address"
                    control={control}
                    rules={{
                      required:
                        type === 'add' ? 'IP Address is required' : undefined,
                      validate: (value: string | undefined) => {
                        const validationResult = validateIPAddress(value || '');
                        return (
                          validationResult || 'Please enter a valid ip address'
                        );
                      }
                    }}
                    render={({ field }) => (
                      <FormControl id="ip-address" fullWidth>
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          error={!!errors.ip_address}
                          helperText={errors.ip_address?.message}
                          onChange={(event) => {
                            field.onChange(event.target.value);
                            // Add to protected attributes when user makes an edit
                            if (
                              type === 'edit' &&
                              !updatedProtectedAttributes.includes('ip_address')
                            ) {
                              setUpdatedProtectedAttributes([
                                ...updatedProtectedAttributes,
                                'ip_address'
                              ]);
                            }
                          }}
                        />
                      </FormControl>
                    )}
                  />
                  {type === 'edit' &&
                    dataProvider !== ASSET_DATA_PROVIDERS.MANUAL &&
                    updatedProtectedAttributes.includes('ip_address') && (
                      <Tooltip title={ASSET_TOOLTIP_TEXT}>
                        <IconButton
                          size="small"
                          onMouseEnter={() => setHoveredField('ip_address')}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => {
                            setUpdatedProtectedAttributes(
                              updatedProtectedAttributes.filter(
                                (attr) => attr !== 'ip_address'
                              )
                            );
                            setValue(
                              'ip_address',
                              selectedAsset?.ip_address || ''
                            );
                          }}
                        >
                          <FontAwesomeIcon
                            icon={
                              hoveredField === 'ip_address'
                                ? faUserXmark
                                : faUserPen
                            }
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                </Stack>
              </Box>
              {/* Asset Type Field */}
              <Box className={classes.assetHalfWidthField}>
                <InputLabel required={type === 'add'} htmlFor="asset-type">
                  Asset Type
                </InputLabel>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center' }}
                  spacing={2}
                >
                  <Controller
                    name="asset_type"
                    control={control}
                    rules={{ required: 'Asset Type is required' }}
                    render={({ field }) => (
                      <FormControl
                        id="asset-type"
                        fullWidth
                        error={!!errors.asset_type}
                      >
                        <Select {...field} onChange={handleTypeChange}>
                          {assetTypes.map((assetType) => (
                            <MenuItem key={assetType} value={assetType}>
                              {assetType}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.asset_type && (
                          <FormHelperText>
                            {errors.asset_type.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  {type === 'edit' &&
                    dataProvider !== ASSET_DATA_PROVIDERS.MANUAL &&
                    updatedProtectedAttributes.includes('asset_type') && (
                      <Tooltip title={ASSET_TOOLTIP_TEXT}>
                        <IconButton
                          size="small"
                          onMouseEnter={() => setHoveredField('asset_type')}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => {
                            setUpdatedProtectedAttributes(
                              updatedProtectedAttributes.filter(
                                (attr) => attr !== 'asset_type'
                              )
                            );
                            setValue(
                              'asset_type',
                              selectedAsset?.asset_type || ''
                            );
                          }}
                        >
                          <FontAwesomeIcon
                            icon={
                              hoveredField === 'asset_type'
                                ? faUserXmark
                                : faUserPen
                            }
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                </Stack>
              </Box>
              {/* Sub Type Field */}
              <Box className={classes.assetHalfWidthField}>
                <InputLabel required={type === 'add'} htmlFor="asset-subtype">
                  Sub Type
                </InputLabel>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center' }}
                  spacing={2}
                >
                  <Controller
                    name="asset_subtype"
                    control={control}
                    rules={{
                      required: 'Sub Type is required'
                    }}
                    render={({ field }) => (
                      <FormControl
                        id="asset-subtype"
                        fullWidth
                        error={!!errors.asset_subtype}
                      >
                        <Select
                          disabled={
                            selectedAssetType === null ||
                            selectedAssetType === undefined ||
                            selectedAssetType === '' ||
                            !selectedAssetType
                          }
                          {...field}
                          onChange={handleSubTypeChange}
                        >
                          {assetSubTypes.map((assetSubType) => (
                            <MenuItem key={assetSubType} value={assetSubType}>
                              {assetSubType}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.asset_subtype && (
                          <FormHelperText>
                            {errors.asset_subtype.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  {type === 'edit' &&
                    dataProvider !== ASSET_DATA_PROVIDERS.MANUAL &&
                    updatedProtectedAttributes.includes('asset_subtype') && (
                      <Tooltip title={ASSET_TOOLTIP_TEXT}>
                        <IconButton
                          size="small"
                          onMouseEnter={() => setHoveredField('asset_subtype')}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => {
                            setUpdatedProtectedAttributes(
                              updatedProtectedAttributes.filter(
                                (attr) => attr !== 'asset_subtype'
                              )
                            );
                            setValue(
                              'asset_subtype',
                              selectedAsset?.asset_subtype || ''
                            );
                          }}
                        >
                          <FontAwesomeIcon
                            icon={
                              hoveredField === 'asset_subtype'
                                ? faUserXmark
                                : faUserPen
                            }
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                </Stack>
              </Box>
              {/* Operating System Field */}
              <Box className={classes.assetHalfWidthField}>
                <InputLabel
                  required={type === 'add'}
                  htmlFor="operating-system"
                >
                  Operating System
                </InputLabel>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center' }}
                  spacing={2}
                >
                  <Controller
                    name="operating_system"
                    control={control}
                    rules={
                      type === 'add'
                        ? { required: 'Operating System is required' }
                        : {}
                    }
                    render={({ field }) => (
                      <FormControl fullWidth id="operating-system">
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          error={!!errors.operating_system}
                          helperText={errors.operating_system?.message}
                          onChange={(event) => {
                            field.onChange(event.target.value);
                            // Add to protected attributes when user makes an edit
                            if (
                              type === 'edit' &&
                              !updatedProtectedAttributes.includes(
                                'operating_system'
                              )
                            ) {
                              setUpdatedProtectedAttributes([
                                ...updatedProtectedAttributes,
                                'operating_system'
                              ]);
                            }
                          }}
                        />
                      </FormControl>
                    )}
                  />
                  {type === 'edit' &&
                    dataProvider !== ASSET_DATA_PROVIDERS.MANUAL &&
                    updatedProtectedAttributes.includes('operating_system') && (
                      <Tooltip title={ASSET_TOOLTIP_TEXT}>
                        <IconButton
                          size="small"
                          onMouseEnter={() =>
                            setHoveredField('operating_system')
                          }
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => {
                            setUpdatedProtectedAttributes(
                              updatedProtectedAttributes.filter(
                                (attr) => attr !== 'operating_system'
                              )
                            );
                          }}
                        >
                          <FontAwesomeIcon
                            icon={
                              hoveredField === 'operating_system'
                                ? faUserXmark
                                : faUserPen
                            }
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                </Stack>
              </Box>
              {/* Domain Field */}
              <Box className={classes.assetHalfWidthField}>
                <InputLabel htmlFor="domain">Domain</InputLabel>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center' }}
                  spacing={2}
                >
                  <Controller
                    name="domain"
                    control={control}
                    render={({ field }) => (
                      <FormControl id="domain" fullWidth>
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          error={!!errors.domain}
                          helperText={errors.domain?.message}
                          onChange={(event) => {
                            field.onChange(event.target.value);
                            // Add to protected attributes when user makes an edit
                            if (
                              type === 'edit' &&
                              !updatedProtectedAttributes.includes('domain')
                            ) {
                              setUpdatedProtectedAttributes([
                                ...updatedProtectedAttributes,
                                'domain'
                              ]);
                            }
                          }}
                        />
                      </FormControl>
                    )}
                  />
                  {type === 'edit' &&
                    dataProvider !== ASSET_DATA_PROVIDERS.MANUAL &&
                    updatedProtectedAttributes.includes('domain') && (
                      <Tooltip title={ASSET_TOOLTIP_TEXT}>
                        <IconButton
                          size="small"
                          onMouseEnter={() => setHoveredField('domain')}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => {
                            setUpdatedProtectedAttributes(
                              updatedProtectedAttributes.filter(
                                (attr) => attr !== 'domain'
                              )
                            );
                            setValue('domain', selectedAsset?.domain || '');
                          }}
                        >
                          <FontAwesomeIcon
                            icon={
                              hoveredField === 'domain'
                                ? faUserXmark
                                : faUserPen
                            }
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                </Stack>
              </Box>
              {/* Criticality Field */}
              <Box className={classes.assetHalfWidthField}>
                <InputLabel htmlFor="criticality">Criticality</InputLabel>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center' }}
                  spacing={2}
                >
                  <Controller
                    name="criticality"
                    control={control}
                    render={({ field }) => (
                      <FormControl
                        id="criticality"
                        fullWidth
                        error={!!errors.criticality}
                      >
                        <Select
                          value={field.value}
                          onChange={(event) => {
                            field.onChange(event.target.value);
                            // Add to protected attributes when user makes an edit
                            if (
                              type === 'edit' &&
                              !updatedProtectedAttributes.includes(
                                'criticality'
                              )
                            ) {
                              setUpdatedProtectedAttributes([
                                ...updatedProtectedAttributes,
                                'criticality'
                              ]);
                            }
                          }}
                          input={<OutlinedInput />}
                        >
                          {Object.entries(CRITICALITY_LABELS).map(
                            ([key, value]) => (
                              <MenuItem key={key} value={value}>
                                {value}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    )}
                  />
                  {type === 'edit' &&
                    dataProvider !== ASSET_DATA_PROVIDERS.MANUAL &&
                    updatedProtectedAttributes.includes('criticality') && (
                      <Tooltip title={ASSET_TOOLTIP_TEXT}>
                        <IconButton
                          size="small"
                          onMouseEnter={() => setHoveredField('criticality')}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => {
                            setUpdatedProtectedAttributes(
                              updatedProtectedAttributes.filter(
                                (attr) => attr !== 'criticality'
                              )
                            );
                            setValue(
                              'criticality',
                              selectedAsset?.criticality || ''
                            );
                          }}
                        >
                          <FontAwesomeIcon
                            icon={
                              hoveredField === 'criticality'
                                ? faUserXmark
                                : faUserPen
                            }
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                </Stack>
              </Box>
              {/* Compliance Tags Field */}
              <Box className={classes.assetHalfWidthField}>
                <InputLabel htmlFor="compliance-tags">
                  Compliance Tags
                </InputLabel>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center' }}
                  spacing={2}
                >
                  <Controller
                    name="compliance_tags"
                    control={control}
                    render={({ field }) => (
                      <FormControl
                        id="compliance-tags"
                        fullWidth
                        error={!!errors.compliance_tags}
                      >
                        <Select
                          multiple
                          value={field.value || []}
                          onChange={handleComplianceTagsChange}
                          input={<OutlinedInput />}
                          renderValue={(selected) => (
                            <Box className={classes.complianceTagsContainer}>
                              {selected.map((value) => (
                                <Chip
                                  key={value}
                                  label={value}
                                  size="small"
                                  onMouseDown={(event) =>
                                    event.stopPropagation()
                                  }
                                  onDelete={() => handleTagDelete(value)}
                                />
                              ))}
                            </Box>
                          )}
                        >
                          {COMPLIANCE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.compliance_tags && (
                          <FormHelperText>
                            {errors.compliance_tags.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  {type === 'edit' &&
                    dataProvider !== ASSET_DATA_PROVIDERS.MANUAL &&
                    updatedProtectedAttributes.includes('compliance_tags') && (
                      <Tooltip title={ASSET_TOOLTIP_TEXT}>
                        <IconButton
                          size="small"
                          onMouseEnter={() =>
                            setHoveredField('compliance_tags')
                          }
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => {
                            setUpdatedProtectedAttributes(
                              updatedProtectedAttributes.filter(
                                (attr) => attr !== 'compliance_tags'
                              )
                            );
                            setValue(
                              'compliance_tags',
                              extractTagNames(selectedAsset?.compliance_tags) ||
                                []
                            );
                          }}
                        >
                          <FontAwesomeIcon
                            icon={
                              hoveredField === 'compliance_tags'
                                ? faUserXmark
                                : faUserPen
                            }
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                </Stack>
              </Box>
              {/* Tags Field */}
              <Box className={classes.assetHalfWidthField}>
                <InputLabel htmlFor="user-tags">Tags</InputLabel>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center' }}
                  spacing={2}
                >
                  <Controller
                    name="user_tags"
                    control={control}
                    render={({ field }) => (
                      <FormControl id="user-tags" fullWidth>
                        <Autocomplete
                          sx={{ width: '100%' }}
                          multiple
                          freeSolo
                          value={combinedTagsValue}
                          onChange={(_, newValue: string[]) => {
                            // Preserve fixed options (IP mapping tags) in the value
                            const editableValues = newValue.filter(
                              (option) => !ipMappingTagNames.includes(option)
                            );
                            field.onChange(editableValues);

                            // Add to protected attributes when user makes an edit
                            if (
                              type === 'edit' &&
                              !updatedProtectedAttributes.includes('user_tags')
                            ) {
                              setUpdatedProtectedAttributes([
                                ...updatedProtectedAttributes,
                                'user_tags'
                              ]);
                            }
                          }}
                          options={allTagOptions}
                          renderValue={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => {
                              const isFixed =
                                ipMappingTagNames.includes(option);
                              const { key, ...tagProps } = getTagProps({
                                index
                              });

                              return (
                                <Chip
                                  key={key}
                                  {...tagProps}
                                  label={option}
                                  onDelete={
                                    isFixed ? undefined : tagProps.onDelete
                                  }
                                  variant={isFixed ? 'outlined' : 'filled'}
                                  color={isFixed ? 'default' : 'primary'}
                                />
                              );
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              error={!!errors.user_tags}
                              helperText={errors.user_tags?.message}
                              placeholder="Type and press Enter to add tags"
                            />
                          )}
                        />
                      </FormControl>
                    )}
                  />
                  {type === 'edit' &&
                    dataProvider !== ASSET_DATA_PROVIDERS.MANUAL &&
                    updatedProtectedAttributes.includes('tags') && (
                      <Tooltip title={ASSET_TOOLTIP_TEXT}>
                        <IconButton
                          size="small"
                          onMouseEnter={() => setHoveredField('tags')}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => {
                            setUpdatedProtectedAttributes(
                              updatedProtectedAttributes.filter(
                                (attr) => attr !== 'tags'
                              )
                            );
                            setValue(
                              'user_tags',
                              extractTagNames(selectedAsset?.user_tags || [])
                            );
                          }}
                        >
                          <FontAwesomeIcon
                            icon={
                              hoveredField === 'tags' ? faUserXmark : faUserPen
                            }
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                </Stack>
              </Box>
            </Stack>
            {/* Description Field */}
            <Box sx={{ mb: 3 }}>
              <InputLabel htmlFor="description">Description</InputLabel>
              <Stack direction="row" sx={{ alignItems: 'center' }} spacing={2}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <FormControl id="description" fullWidth>
                      <TextField
                        {...field}
                        multiline
                        fullWidth
                        error={!!errors.description}
                        helperText={errors.description?.message}
                        onChange={(event) => {
                          field.onChange(event.target.value);
                          // Add to protected attributes when user makes an edit
                          if (
                            type === 'edit' &&
                            !updatedProtectedAttributes.includes('description')
                          ) {
                            setUpdatedProtectedAttributes([
                              ...updatedProtectedAttributes,
                              'description'
                            ]);
                          }
                        }}
                      />
                    </FormControl>
                  )}
                />
                {type === 'edit' &&
                  dataProvider !== ASSET_DATA_PROVIDERS.MANUAL &&
                  updatedProtectedAttributes.includes('description') && (
                    <Tooltip title={ASSET_TOOLTIP_TEXT}>
                      <IconButton
                        size="small"
                        onMouseEnter={() => setHoveredField('description')}
                        onMouseLeave={() => setHoveredField(null)}
                        onClick={() => {
                          setUpdatedProtectedAttributes(
                            updatedProtectedAttributes.filter(
                              (attr) => attr !== 'description'
                            )
                          );
                          setValue(
                            'description',
                            selectedAsset?.description || ''
                          );
                        }}
                      >
                        <FontAwesomeIcon
                          icon={
                            hoveredField === 'description'
                              ? faUserXmark
                              : faUserPen
                          }
                        />
                      </IconButton>
                    </Tooltip>
                  )}
              </Stack>
            </Box>
            {(type === 'edit' || type === 'add') && (
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hostIsolationExcluded}
                      disabled={
                        !canUpdateSettings ||
                        isSettingsLoading ||
                        isSettingsError ||
                        !organizationSettings ||
                        (type === 'edit' &&
                          (!assetDetailReady ||
                            isAssetDetailFetching ||
                            isAssetDetailError))
                      }
                      onChange={(_, checked) =>
                        setHostIsolationExcluded(checked)
                      }
                    />
                  }
                  label="Exclude asset from isolation/disconnect actions."
                />
                {isSettingsLoading && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center' }}
                  >
                    <CircularProgress size={16} />
                    <FormHelperText>
                      Loading organization settings
                    </FormHelperText>
                  </Stack>
                )}
                {isSettingsError && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center' }}
                  >
                    <FormHelperText error>
                      Settings could not be loaded
                    </FormHelperText>
                    <Button size="small" onClick={() => void refetchSettings()}>
                      Retry
                    </Button>
                  </Stack>
                )}
                <FormHelperText>
                  Adds or removes this asset&apos;s hostname from the
                  organization host isolation exclusions when this edit is
                  saved.
                </FormHelperText>
              </Box>
            )}
            {settingsSaveError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {settingsSaveError}
              </Alert>
            )}
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button
          onClick={handleCancel}
          disabled={isSubmitting || isUpdatingHostIsolationSettings}
        >
          Cancel
        </Button>
        {!IS_ALL_ORG && (
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={
              isSubmitting ||
              isLoadingCreateAsset ||
              isLoadingUpdateAssetIntegration ||
              isUpdatingHostIsolationSettings ||
              (type === 'edit' && !assetDetailReady)
            }
          >
            {isSubmitting ||
            isLoadingCreateAsset ||
            isLoadingUpdateAssetIntegration ||
            isUpdatingHostIsolationSettings
              ? 'Saving...'
              : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssetAddEditDialog;
